import { useCallback, useState } from 'react';

interface UseFormWithValidationOptions<T, R = unknown> {
  onSubmit: (data: T) => Promise<R>;
  onSuccess?: (result: R) => void;
  onError?: (error: unknown) => void;
  validateFn?: (data: T) => string | null;
}

interface UseFormWithValidationReturn<T> {
  isSubmitting: boolean;
  error: string | { key: string; params?: Record<string, unknown> };
  handleSubmit: (e: React.FormEvent, data: T) => Promise<void>;
  setError: (
    error: string | { key: string; params?: Record<string, unknown> }
  ) => void;
  clearError: () => void;
}

export function useFormWithValidation<T = unknown, R = unknown>({
  onSubmit,
  onSuccess,
  onError,
  validateFn,
}: UseFormWithValidationOptions<T, R>): UseFormWithValidationReturn<T> {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<
    string | { key: string; params?: Record<string, unknown> }
  >('');

  const handleSubmit = useCallback(
    async (e: React.FormEvent, data: T) => {
      e.preventDefault();

      // Clear any previous errors
      setError('');

      // Validate if validation function provided
      if (validateFn) {
        const validationError = validateFn(data);
        if (validationError) {
          setError(validationError);
          return;
        }
      }

      setIsSubmitting(true);
      try {
        const result = await onSubmit(data);
        onSuccess?.(result);
      } catch (err: unknown) {
        // Check if error has translationKey
        const errorWithResponse = err as {
          response?: {
            data?: {
              error?: {
                translationKey?: string;
                translationParams?: Record<string, unknown>;
              };
            };
          };
          translationKey?: string;
          translationParams?: Record<string, unknown>;
          message?: string;
        };

        if (errorWithResponse?.response?.data?.error?.translationKey) {
          setError({
            key: errorWithResponse.response.data.error.translationKey,
            params: errorWithResponse.response.data.error.translationParams,
          });
        } else if (errorWithResponse?.translationKey) {
          setError({
            key: errorWithResponse.translationKey,
            params: errorWithResponse.translationParams,
          });
        } else if (errorWithResponse?.message) {
          setError(errorWithResponse.message);
        } else {
          setError('An error occurred');
        }
        onError?.(err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, onSuccess, onError, validateFn]
  );

  const clearError = useCallback(() => {
    setError('');
  }, []);

  return {
    isSubmitting,
    error,
    handleSubmit,
    setError,
    clearError,
  };
}
